import React, { useState } from 'react';
import { ShieldCheck, Truck, Sparkles, Check, ShoppingBag, Plus, Minus, Tag, Zap, ChevronRight, Info } from 'lucide-react';
import Button from 'components/ui/Button';
import Reveal from 'components/ui/Reveal';
import { BRAND, PRICING_TIERS, PRODUCT } from 'lib/constants';
import { buildOrderMessage, getWhatsAppUrl } from 'lib/whatsapp';
import ProductImg from '../../images/product/ProductImg.webp';
import ProductImgBack from '../../images/product/ProductImgBack.webp';
import ProductImgOpen from '../../images/product/ProductImgOpen.webp';

export default function OrderSection({ id = "order-now" }) {
  const [selectedPack, setSelectedPack] = useState(PRICING_TIERS[1]); // Default to 2 Packets
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  const images = [ProductImg, ProductImgBack, ProductImgOpen];

  const finalTotalPrice = (selectedPack.finalPrice || 249) * quantity;
  const normalTotalPrice = (selectedPack.normalPrice || 249) * quantity;
  const totalSavingPrice = (selectedPack.saving || 0) * quantity;

  const handleOrderSubmit = () => {
    const message = `Hello Singaraa!\n\nI would like to order:\n*Item:* ${PRODUCT.name}\n*Pack Size:* ${selectedPack.title}\n*Quantity:* ${quantity} Set(s)\n*Total Amount:* ₹${finalTotalPrice}\n\nPlease share payment details and ask for my delivery address.`;
    window.open(getWhatsAppUrl(message), '_blank');
  };

  return (
    <section id={id} className="pt-6 sm:pt-8 pb-12 sm:pb-16 md:pb-20 lg:pb-24 bg-[#Fdfbf7] relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-10">

        {/* Breadcrumb / Top Info */}
        <Reveal>
          <div className="text-xs sm:text-sm text-singaraa-dark/60 font-body mb-6 sm:mb-10 flex items-center gap-2">
            <span>Home</span> <ChevronRight className="w-3 h-3" /> <span>Products</span> <ChevronRight className="w-3 h-3" /> <span className="font-semibold text-singaraa-dark">Premium Roasted Peanuts</span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

          {/* Left Column - Product Images */}
          <div className="lg:col-span-6 lg:col-start-1 flex flex-col gap-4">
            <Reveal delay={0.1}>
              <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.1)] border border-singaraa-caramel/20 flex items-center justify-center relative overflow-hidden h-[400px] sm:h-[500px] md:h-[600px] group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-singaraa-caramel/5 rounded-full blur-3xl pointer-events-none transition-all group-hover:scale-110" />
                <img
                  src={images[activeImage]}
                  alt="Singaraa Peanuts"
                  className="w-full h-full object-contain drop-shadow-2xl transition-transform duration-700 hover:scale-105 z-10"
                />
                {selectedPack.badge && (
                  <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20">
                    <span className="bg-singaraa-brown text-white text-xs sm:text-sm font-bold px-4 py-2 rounded-full shadow-lg border border-singaraa-brown/50">
                      {selectedPack.badge}
                    </span>
                  </div>
                )}
              </div>
            </Reveal>

            {/* Thumbnails */}
            <Reveal delay={0.2}>
              <div className="flex gap-3 sm:gap-4 overflow-x-auto py-2 scrollbar-hide px-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`flex-shrink-0 w-20 sm:w-24 h-20 sm:h-24 bg-white rounded-xl sm:rounded-2xl border-2 p-2.5 transition-all duration-300 overflow-hidden ${activeImage === idx ? 'border-singaraa-brown shadow-lg scale-[1.02]' : 'border-singaraa-caramel/20 hover:border-singaraa-caramel/60 hover:shadow-md opacity-70 hover:opacity-100'}`}
                  >
                    <img src={img} alt={`Thumbnail ${idx}`} className="w-full h-full object-contain drop-shadow-md" />
                  </button>
                ))}
              </div>
            </Reveal>

            {/* Description (Desktop) */}
            <Reveal delay={0.3} className="hidden lg:block mt-8">
              <div className="border-t border-singaraa-caramel/20 pt-8">
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-singaraa-dark mb-4">Product Description</h3>
                <p className="text-singaraa-dark/80 font-body leading-relaxed mb-6 text-sm sm:text-base">
                  {PRODUCT.description} We carefully select the finest peanuts, slow-roast them to perfection without frying, and season them just right. The result? A crunchy, protein-rich snack that brings you the authentic taste of Kathiyawad.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-5 rounded-2xl border border-singaraa-caramel/15 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="text-[11px] font-bold text-singaraa-brown uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Ingredients</h4>
                    <p className="text-sm text-singaraa-dark font-medium">{PRODUCT.ingredients}</p>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-singaraa-caramel/15 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="text-[11px] font-bold text-singaraa-brown uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Shelf Life</h4>
                    <p className="text-sm text-singaraa-dark font-medium">{PRODUCT.shelfLife}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Column - Buying Interface */}
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col">
            <Reveal delay={0.15}>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-singaraa-dark mb-3 leading-tight">
                {PRODUCT.name}
              </h1>
              <p className="text-singaraa-brown font-semibold text-sm sm:text-base mb-6">
                {PRODUCT.tagline}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6 bg-white p-4 rounded-2xl border border-singaraa-caramel/15 shadow-sm inline-flex">
                <span className="font-heading text-4xl sm:text-5xl font-black text-singaraa-dark">
                  ₹{selectedPack.finalPrice}
                </span>
                {selectedPack.saving > 0 && (
                  <div className="flex flex-col items-start">
                    <span className="text-base text-singaraa-dark/40 line-through">
                      ₹{selectedPack.normalPrice}
                    </span>
                    <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200 mt-1">
                      Save ₹{selectedPack.saving}
                    </span>
                  </div>
                )}
              </div>



              {/* Pack Size Selector */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-singaraa-dark text-lg">Select Pack Size</h3>
                  <span className="text-xs font-semibold text-singaraa-brown bg-singaraa-cream px-2.5 py-1 rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" /> More packs = More savings
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {PRICING_TIERS.map((pack) => {
                    const isSelected = selectedPack.id === pack.id;
                    return (
                      <button
                        key={pack.id}
                        onClick={() => setSelectedPack(pack)}
                        className={`relative p-4 rounded-2xl border-2 text-left transition-all duration-300 ${isSelected
                          ? 'bg-white border-singaraa-brown shadow-[0_5px_15px_rgba(0,0,0,0.08)] ring-2 ring-singaraa-brown/10 scale-[1.01]'
                          : 'bg-white border-singaraa-caramel/20 hover:border-singaraa-caramel hover:shadow-md'
                          }`}
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <span className="font-heading font-bold text-singaraa-dark text-[15px]">{pack.title}</span>
                          <span className={`font-bold ${isSelected ? 'text-singaraa-brown' : 'text-singaraa-dark'}`}>₹{pack.finalPrice}</span>
                        </div>
                        <div className="text-[12px] text-singaraa-dark/60 leading-tight">
                          {pack.normalPrice > pack.finalPrice && (
                            <span className="line-through mr-1.5 text-singaraa-dark/40">₹{pack.normalPrice}</span>
                          )}
                          {pack.description}
                        </div>
                        {pack.badge && (
                          <span className={`absolute -top-3 right-3 px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white uppercase tracking-wider shadow-sm ${pack.badge === 'Best Value' ? 'bg-singaraa-brown' : pack.badge === 'Popular' ? 'bg-[#25D366]' : 'bg-singaraa-caramel'
                            }`}>
                            {pack.badge}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Quantity and Buy Button */}
              <div className="flex flex-col sm:flex-row items-stretch gap-4 mb-8">
                <div className="flex items-center justify-between bg-white px-4 py-1.5 rounded-2xl border-2 border-singaraa-caramel/20 w-full sm:w-36 shadow-sm">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="text-singaraa-dark hover:text-singaraa-brown hover:bg-singaraa-cream w-8 h-8 rounded-lg flex items-center justify-center transition-colors">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-heading font-bold text-xl w-8 text-center">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="text-singaraa-dark hover:text-singaraa-brown hover:bg-singaraa-cream w-8 h-8 rounded-lg flex items-center justify-center transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <Button
                  onClick={handleOrderSubmit}
                  variant="whatsapp"
                  size="lg"
                  className="flex-1 justify-center text-base sm:text-lg shadow-xl shadow-[#25D366]/20 group h-14 sm:h-auto rounded-2xl"
                >
                  Buy Now - ₹{finalTotalPrice.toLocaleString('en-IN')}
                  <ShoppingBag className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" />
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 border border-singaraa-caramel/15 shadow-sm space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-singaraa-cream flex items-center justify-center shrink-0">
                    <Truck className="w-6 h-6 text-singaraa-brown" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-[15px] text-singaraa-dark mb-0.5">Fast Pan-India Delivery</h4>
                    <p className="text-[13px] text-singaraa-dark/70 font-body">Freshly packed and dispatched within 24 hours to your doorstep.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-singaraa-cream flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-6 h-6 text-singaraa-brown" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-[15px] text-singaraa-dark mb-0.5">Direct Brand Guarantee</h4>
                    <p className="text-[13px] text-singaraa-dark/70 font-body">100% dry roasted, premium quality assurance directly from founders.</p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Description (Mobile view) */}
            <Reveal delay={0.3} className="lg:hidden mt-10">
              <div className="border-t border-singaraa-caramel/20 pt-8">
                <h3 className="font-heading text-2xl font-bold text-singaraa-dark mb-4">Product Description</h3>
                <p className="text-singaraa-dark/80 font-body leading-relaxed mb-6 text-sm">
                  {PRODUCT.description} We carefully select the finest peanuts, slow-roast them to perfection without frying, and season them just right. The result? A crunchy, protein-rich snack that brings you the authentic taste of Kathiyawad.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl border border-singaraa-caramel/15 shadow-sm">
                    <h4 className="text-[11px] font-bold text-singaraa-brown uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Ingredients</h4>
                    <p className="text-sm text-singaraa-dark font-medium">{PRODUCT.ingredients}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-singaraa-caramel/15 shadow-sm">
                    <h4 className="text-[11px] font-bold text-singaraa-brown uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Shelf Life</h4>
                    <p className="text-sm text-singaraa-dark font-medium">{PRODUCT.shelfLife}</p>
                  </div>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </div>
    </section>
  );
}
